import {AfterViewInit, Component, computed, inject, signal, ViewChild,} from "@angular/core";
import {ActivatedRoute, ParamMap, Router} from "@angular/router";
import {
  BehaviorSubject,
  combineLatest,
  filter,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  shareReplay,
  startWith,
  switchMap,
} from "rxjs";
import {ApiService, Tables} from "../../services/api.service";
import {CommonModule, formatDate} from "@angular/common";
import {BootstrapIcon, HeaderService} from "../../services/header.service";
import {Workstation} from "../../shared/interfaces/workstation";
import {MalfunctionStatus} from "../../shared/interfaces/report";
import {SearchFilterBarComponent} from "../../shared/components/search-filter-bar/search-filter-bar.component";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {
  FilterInterfaceComponent,
  FilterParams,
} from "../../shared/components/filter-interface/filter-interface.component";
import {WorkplaceCardComponent} from "../workplaces/components/workplace-card/workplace-card.component";
import {PAGE_SIZE, TABLE_CONFIG, TableType} from "../dashboard/components/report-table/table-config";
import {SearchService} from "../knowledge-database/data-access/search.service";
import {Workplace} from "../../shared/interfaces/workplace";
import {PieChartComponent, PieChartData} from "../../shared/components/charts/pie-chart/pie-chart.component";
import {NavigationService} from "../../shared/data-access/navigation.service";

export interface StandstillStatistics {
  created_before_solved_within_ms: number | null;
  created_before_unresolved_ms: number | null;
  created_within_unresolved_ms: number | null;
  effective_end_date: string;
  effective_start_date: string;
  report_categories: StandstillCategoryReport[];
  resolved_within_interval_ms: number | null;
  statistics: any;
  total_interval_duration_ms: number;
  total_resolution_time_ms: number;
}

export type StandstillReportCategory =
  "created_before_solved_within_ms"
  | 'created_before_unresolved_ms'
  | 'created_within_unresolved_ms'
  | 'resolved_within_interval_ms'


export interface StandstillCategoryReport {
  category: StandstillReportCategory;
  count: number;
  incidents: StandstillCategoryReportIncident[];
  total_time_ms: number;
}

export interface StandstillCategoryReportIncident {
  incident_id: string;
  report_id: string;
  resolution_time_ms: number;
}

@Component({
  selector: "app-workplace-report",
  templateUrl: "./workplace-report.component.html",
  styleUrls: ["./workplace-report.component.scss"],
  standalone: true,
  imports: [
    CommonModule,
    SearchFilterBarComponent,
    FilterInterfaceComponent,
    WorkplaceCardComponent,
    PieChartComponent,
  ],
})
export class WorkplaceReportComponent implements AfterViewInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private apiService = inject(ApiService);
  private headerService = inject(HeaderService);
  private searchService = inject(SearchService);
  private navigationService = inject(NavigationService);

  protected activeModule = signal<'report' | 'escalation'>('report')
  showFilter = signal<boolean>(true);

  BootstrapIcon = BootstrapIcon;
  @ViewChild("searchBar") searchBar!: SearchFilterBarComponent;
  @ViewChild("reportFilter") reportFilter!: FilterInterfaceComponent;

  activeReports = computed(() => this.searchService.state().activeReports.data);
  solvedReports = computed(() => this.searchService.state().solvedReports.data);
  escalatedReports = computed(() => this.searchService.state().escalatedReports.data);

  totalPages = computed(() => ({
    activeReports: this.searchService.state().activeReports.totalPages,
    solvedReports: this.searchService.state().solvedReports.totalPages,
    escalatedReports: this.searchService.state().escalatedReports.totalPages,
  }))

  selectedWorkplace = signal<Workplace | null>(null);
  filterParams = signal<FilterParams | null>(null);
  chartData = signal<PieChartData | null>(null)

  private workplaceId$: Observable<ParamMap> = this.route.paramMap;
  private searchTerm$ = new BehaviorSubject<string | null>("");


  ngAfterViewInit() {
    this.searchBar.valueChange
      .pipe(
        startWith(""),
        map((searchTerm) => searchTerm || null)
      )
      .subscribe((value) => {
        this.searchTerm$.next(value);
      });
  }

  onFilterValueChange(filterParams: FilterParams) {
    this.filterParams.set(filterParams);
    this.searchService.setFilterValues(filterParams);
    this.apiService.getChartData({
      filterParams: filterParams,
      workplaceFilter: this.selectedWorkplace()?.id,
      productionLineFilter: null

    }).subscribe((data) => {
      this.createChartData(data);
    });
  }

  getQundisColor(colorName: string) {
    return getComputedStyle(document.documentElement).getPropertyValue(`--qundis-${colorName}`).trim();
  }

  setPage(event: { page: number, tableType: TableType }) {
    const {page, tableType} = event
    this.searchService.setPage(tableType, page);
  }


  constructor() {
    this.activeModule.set(this.activatedRoute.snapshot.queryParams['module'])
    this.headerService.setHeaderIcon(BootstrapIcon.workplaces);
    this.headerService.setHeaderTitle(
      this.activeModule() === 'report'
        ? "Stillstandshistorie"
        : "Eskalationsübersicht"
    );
    this.headerService.setHeaderSubTitle('Knatterbox')

    this.workplaceId$
      .pipe(
        map((params) => params.get("id")),
        filter((id) => !!id),
        switchMap((id) => this.apiService.getItemByIdentifier({
          relation: Tables.Workplace,
          identifierKey: 'id',
          identifierValue: id!
        })),
        takeUntilDestroyed()
      )
      .subscribe((workplace: Workplace) => {
        this.selectedWorkplace.set(workplace);
        this.headerService.setHeaderSubTitle(workplace.name)
        this.searchService.setWorkplaceFilter(workplace.id)
      });
  }

  onValueChange(searchTerm: string) {
    this.searchService.setSearchTerm(searchTerm);
  }

  createChartData(data: StandstillStatistics) {
    const TOTAL_HOURS = this.millisecondsToHours(data.total_interval_duration_ms);
    /*const standstillCreatedAndResolvedWithin = this.millisecondsToHours(data.total_resolved_within_interval_ms);
    const standstillCreatedBeforeSolvedWithin = this.millisecondsToHours(data.created_before_solved_within_ms);
    const standstillCreatedWithinUnresolved = this.millisecondsToHours(data.created_within_unresolved_ms);
    const standstillCreatedBeforeUnresolved = this.millisecondsToHours(data.created_before_unresolved_ms);*/
    const totalStandstillTime = this.millisecondsToHours(data.total_resolution_time_ms);
    const chartData = {
      labels: ['Stillstand', 'Betrieb'],
      datasets: [
        {
          label: 'Zeit in h',
          data: [totalStandstillTime > TOTAL_HOURS ? TOTAL_HOURS : totalStandstillTime, Math.max(TOTAL_HOURS - totalStandstillTime, 0)],
          backgroundColor: [this.getQundisColor('chart-standstill'), this.getQundisColor('chart-idle')],
          hoverOffset: 2
        }
      ]
    };
    this.chartData.set(chartData);
  }

  millisecondsToHours(milliseconds: number) {
    return milliseconds / 1000 / 60 / 60;
  }

  getFilterTimeInterval() {
    if (this.filterParams()?.startDate && this.filterParams()?.endDate) {
      // startdate and enddate are set - return startdate - enddate
      return `${formatDate(this.filterParams()?.startDate!, 'dd.MM.YYYY', 'de-de')} -
      ${formatDate(this.filterParams()?.endDate!, 'dd.MM.YYYY', 'de-de')}`;
    } else if (this.filterParams()?.startDate && !this.filterParams()?.endDate) {
      // only startdate is set - return startdate - current date
      return `${formatDate(this.filterParams()?.startDate!, 'dd.MM.YYYY', 'de-de')} -
      ${formatDate(new Date(), 'dd.MM.YYYY', 'de-de')}`
    } else if (!this.filterParams()?.startDate && this.filterParams()?.endDate) {
      // only enddate is set - return till enddate
      return `bis zum ${formatDate(this.filterParams()?.endDate!, 'dd.MM.YYYY', 'de-de')}`
    } else {
      // no date is set - return till current date
      return `bis zum ${formatDate(new Date(), 'dd.MM.YYYY', 'de-de')}`
    }
  }

  navigateBack() {
    this.navigationService.navigateBack()
  }
}
